import { useState } from 'react';
import { Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { parseMoneyInput } from './parse';

export interface MoneyInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<InputRef>;
  navRow?: number;
  navCol?: number;
}

/**
 * Денежная сумма, до 2 знаков после точки (§1). Запятая трактуется как точка. Коммит на
 * blur/Enter. Неверный ввод (минус, три знака, буквы) не исправляется молча: текст остаётся в
 * поле, поле краснеет и объясняет причину, наружу уходит «пусто».
 */
export function MoneyInput({ value, onChange, autoFocus, disabled, onKeyDown, inputRef, navRow, navCol }: MoneyInputProps) {
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    // См. IntInput: собственный отказ не стирает введённый текст.
    if (!(value === null && error !== null)) {
      setDraft(value ?? '');
      setError(null);
    }
  }

  const commit = () => {
    const parsed = parseMoneyInput(draft);
    setError(parsed.error);
    if (parsed.error === null) setDraft(parsed.value ?? '');
    if (parsed.value !== value) onChange(parsed.value);
  };

  const classes = [navRow !== undefined ? 'nav-cell' : '', error ? 'input-invalid' : ''].filter(Boolean).join(' ');

  return (
    <Tooltip title={error ?? ''} open={error ? undefined : false} color="#cf1322">
      <Input
        ref={inputRef}
        value={draft}
        inputMode="decimal"
        autoFocus={autoFocus}
        disabled={disabled}
        status={error ? 'error' : undefined}
        aria-invalid={error ? true : undefined}
        className={classes || undefined}
        data-nav-row={navRow}
        data-nav-col={navCol}
        style={{ textAlign: 'right', width: '100%' }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          onKeyDown?.(e);
        }}
      />
    </Tooltip>
  );
}
